import { fixed, ipmLabel, rpmLabel, trim } from './format';
import { requirePositive, step, type Solved } from './types';

/**
 * The shop constant. 12/π = 3.8197…, rounded to 3.82 — this is the number in
 * every trade textbook and on every shop wall chart, and it is the number she
 * will be marked against on a test. `exact: true` switches to 12/π for the
 * calculators, where being 0.008% closer costs nothing.
 */
export const SHOP_CONSTANT = 3.82;
export const EXACT_CONSTANT = 12 / Math.PI;

export interface SpeedOptions {
  /** Use 12/π instead of the 3.82 shop constant. Default false. */
  readonly exact?: boolean;
}

/** Spindle speed from surface speed and cutter diameter. */
export function rpmFromSfm(
  sfm: number,
  diameterInches: number,
  options: SpeedOptions = {},
): Solved<number> {
  requirePositive(sfm, 'SFM');
  requirePositive(diameterInches, 'diameter');

  const k = options.exact ? EXACT_CONSTANT : SHOP_CONSTANT;
  const numerator = k * sfm;
  const value = numerator / diameterInches;

  return {
    value,
    steps: [
      step('formula', `RPM = (${options.exact ? '12 ÷ π' : '3.82'} × SFM) ÷ D`),
      step('substitute', `(${trim(k, 4)} × ${trim(sfm)}) ÷ ${trim(diameterInches)}`),
      step('compute', `${trim(k, 4)} × ${trim(sfm)} = ${trim(numerator, 2)}`),
      step('compute', `${trim(numerator, 2)} ÷ ${trim(diameterInches)} = ${fixed(value, 1)}`),
      step('result', `= ${rpmLabel(value)}`),
      step(
        'note',
        'D is the cutter diameter on a mill, the work diameter on a lathe. Diameter sits on the bottom, so a bigger cutter always means fewer RPM.',
      ),
    ],
  };
}

/** Surface speed from spindle speed — the direction you go when checking a setup. */
export function sfmFromRpm(
  rpm: number,
  diameterInches: number,
  options: SpeedOptions = {},
): Solved<number> {
  requirePositive(rpm, 'RPM');
  requirePositive(diameterInches, 'diameter');

  const k = options.exact ? EXACT_CONSTANT : SHOP_CONSTANT;
  const product = rpm * diameterInches;
  const value = product / k;

  return {
    value,
    steps: [
      step('formula', `SFM = (RPM × D) ÷ ${options.exact ? '(12 ÷ π)' : '3.82'}`),
      step('substitute', `(${trim(rpm, 0)} × ${trim(diameterInches)}) ÷ ${trim(k, 4)}`),
      step('compute', `${trim(rpm, 0)} × ${trim(diameterInches)} = ${trim(product, 3)}`),
      step('result', `= ${fixed(value, 1)} SFM`),
    ],
  };
}

/** Feed rate in inches per minute. */
export function feedRate(rpm: number, flutes: number, chipLoad: number): Solved<number> {
  requirePositive(rpm, 'RPM');
  requirePositive(flutes, 'flute count');
  requirePositive(chipLoad, 'chip load');

  const edgesPerMinute = rpm * flutes;
  const value = edgesPerMinute * chipLoad;

  return {
    value,
    steps: [
      step('formula', 'IPM = RPM × flutes × chip load'),
      step('substitute', `${trim(rpm, 0)} × ${flutes} × ${trim(chipLoad, 4)}`),
      step('compute', `${trim(rpm, 0)} × ${flutes} = ${trim(edgesPerMinute, 0)} cutting edges per minute`),
      step('compute', `${trim(edgesPerMinute, 0)} × ${trim(chipLoad, 4)} = ${fixed(value, 3)}`),
      step('result', `= ${ipmLabel(value)}`),
      step('note', 'Chip load is per tooth, per revolution. Not per minute, not per pass.'),
    ],
  };
}

/** Chip load back out of a feed rate — for diagnosing a finish problem. */
export function chipLoad(ipm: number, rpm: number, flutes: number): Solved<number> {
  requirePositive(ipm, 'IPM');
  requirePositive(rpm, 'RPM');
  requirePositive(flutes, 'flute count');

  const edgesPerMinute = rpm * flutes;
  const value = ipm / edgesPerMinute;

  return {
    value,
    steps: [
      step('formula', 'chip load = IPM ÷ (RPM × flutes)'),
      step('substitute', `${trim(ipm, 3)} ÷ (${trim(rpm, 0)} × ${flutes})`),
      step('compute', `${trim(rpm, 0)} × ${flutes} = ${trim(edgesPerMinute, 0)}`),
      step('result', `= ${fixed(value, 4)}" per tooth`),
      step(
        'note',
        'Thin burnt chips usually mean this number is too low and the tool is rubbing instead of cutting.',
      ),
    ],
  };
}

/** Material removal rate, cubic inches per minute. */
export function removalRate(
  widthOfCut: number,
  depthOfCut: number,
  ipm: number,
): Solved<number> {
  requirePositive(widthOfCut, 'width of cut');
  requirePositive(depthOfCut, 'depth of cut');
  requirePositive(ipm, 'IPM');

  const value = widthOfCut * depthOfCut * ipm;
  return {
    value,
    steps: [
      step('formula', 'MRR = width of cut × depth of cut × IPM'),
      step('substitute', `${trim(widthOfCut)} × ${trim(depthOfCut)} × ${trim(ipm, 3)}`),
      step('result', `= ${fixed(value, 3)} cubic inches per minute`),
    ],
  };
}

/** Cutting time for a straight pass, in minutes. */
export function cutTime(lengthInches: number, ipm: number): Solved<number> {
  requirePositive(lengthInches, 'length');
  requirePositive(ipm, 'IPM');
  const value = lengthInches / ipm;
  return {
    value,
    steps: [
      step('formula', 'minutes = length ÷ IPM'),
      step('substitute', `${trim(lengthInches)} ÷ ${trim(ipm, 3)}`),
      step('result', `= ${fixed(value, 2)} min (${fixed(value * 60, 0)} sec)`),
    ],
  };
}
